import { Linking } from "react-native";

import { MobileAppOnlyDeepLinkURLPath } from "@/app.constants.js";
import type {
  ModalPath,
  ModalPathValue,
  TabPath,
  ValidPath,
} from "@/constants/Navigation";
import { ScreenPath } from "@/constants/Navigation";
import { router } from "@/crossPlatform/expo-router";
import { captureException } from "@/services/AppMonitor";
import {
  isDevelopmentEnvironment,
  isLocalEnvironment,
} from "@/utils/environment.util";

/**
 * Safely encodes a navigation parameter to prevent RegExp parsing errors
 * in Expo Router when the parameter contains special characters like parentheses.
 *
 * @param param - The parameter value to encode
 * @returns The encoded parameter value, or undefined if input is null/undefined
 */
const encodeNavigationParam = (param?: null | string): string | undefined => {
  if (param === null || param === undefined) {
    return undefined;
  }

  return encodeURIComponent(param);
};

/**
 * Safely decodes a navigation parameter that was encoded with encodeNavigationParam.
 *
 * @param param - The encoded parameter value to decode
 * @returns The decoded parameter value, or undefined if input is null/undefined
 */
const decodeNavigationParam = (param?: null | string): string | undefined => {
  if (param === null || param === undefined) {
    return undefined;
  }

  try {
    return decodeURIComponent(param);
  } catch (error) {
    captureException(error);
    // If decoding fails, return the original param to prevent crashes
    return param;
  }
};

/**
 * Safely encodes navigation parameters in an object to prevent RegExp parsing errors.
 *
 * @param params - Object containing navigation parameters
 * @returns Object with encoded parameter values
 */
const encodeNavigationParams = <T extends Record<string, unknown>>(
  params: T,
): Record<string, string | undefined> => {
  const encodedParams: Record<string, string | undefined> = {};

  for (const [key, value] of Object.entries(params)) {
    if (typeof value === "string") {
      encodedParams[key] = encodeNavigationParam(value);
    } else if (value !== null && value !== undefined) {
      // Convert non-string values to string and encode
      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      encodedParams[key] = encodeNavigationParam(String(value));
    } else {
      encodedParams[key] = undefined;
    }
  }

  return encodedParams;
};

const navigateWithParentSelectedUserId = ({
  parentSelectedUserId,
  pathName,
}: {
  parentSelectedUserId?: string;
  pathName: ValueOf<typeof ModalPath>;
}) => {
  router.navigate({
    params: {
      parentSelectedUserId,
    },
    pathname: pathName,
  });
};

type UrlScheme = "sms" | "tel" | "web";

const shouldShowStorybook = isLocalEnvironment() || isDevelopmentEnvironment();

const openUrl = async ({
  onError,
  scheme = "web",
  url,
}: {
  onError: (e?: Error) => void;
  scheme?: UrlScheme;
  url: string;
}) => {
  if (scheme === "web") {
    // only invoke canOpenURL for web URLs; fails on other types of URLs
    const canOpen = await Linking.canOpenURL(url);
    if (!canOpen) {
      onError();
      return;
    }
  }

  const urlWithScheme = scheme === "web" ? url : `${scheme}:${url}`;
  try {
    await Linking.openURL(urlWithScheme);
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
    onError(error as Error);
  }
};

// if deep link came in via /mobile-app/* path, strip the /mobile-app/ to get the actual path
const cleanDeepLinkPath = (path: string) =>
  path.replace(new RegExp(`^${MobileAppOnlyDeepLinkURLPath}`), "/");

// helper functions to prevent likelihood of bug if just used a hardcoded string
const toAbsolutePath = (paths: ValidPath[]): string => `/${paths.join("/")}`;
const toAbsoluteTabPath = (tabPath: TabPath): string =>
  toAbsolutePath([ScreenPath.App, ScreenPath.MainTabs, tabPath]);
const toAbsoluteModalPath = (modalPath: ModalPathValue): string =>
  // ModalPathValues are already prefixed with (modals), so this becomes (app)/(modals)/<modalPath>
  toAbsolutePath([ScreenPath.App, modalPath]);

const openBrowserTab = (url: string) =>
  globalThis.window.open(url, "_blank", "noopener,noreferrer");

export {
  cleanDeepLinkPath,
  decodeNavigationParam,
  encodeNavigationParam,
  encodeNavigationParams,
  MobileAppOnlyDeepLinkURLPath,
  navigateWithParentSelectedUserId,
  openBrowserTab,
  openUrl,
  shouldShowStorybook,
  toAbsoluteModalPath,
  toAbsolutePath,
  toAbsoluteTabPath,
};
